import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ShieldCheck,
  Lock,
  Cpu,
  EyeOff,
  Fingerprint,
  Ghost,
  MessageSquare,
} from "lucide-react";
import { PIXELS, TIERS, ACCESSORIES, HERO_IMAGES, formatAUD, priceFor } from "../mock";

const Phones = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Phantom Phone · Phantom Industries";
  }, []);

  const featured = PIXELS.slice(0, 3);

  return (
    <main className="bg-black text-white" data-testid="phones-page">
      {/* Hero */}
      <section className="relative pt-32 pb-24 md:pb-32 border-b border-white/5 overflow-hidden">
        <div className="absolute inset-0 aurora-purple opacity-70 pointer-events-none" />
        <div className="absolute inset-0 grid-mask pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-14 items-center">
          <div>
            <div className="chip">
              <Ghost className="h-3 w-3" /> Phantom Phone · A Phantom Industries product
            </div>
            <h1
              className="mt-5 font-headline text-5xl md:text-7xl font-bold text-gradient-purple leading-[0.95]"
              data-testid="phones-heading"
            >
              The Pixel.<br />Rebuilt to disappear.
            </h1>
            <p className="mt-6 text-white/70 text-lg leading-relaxed max-w-lg">
              Every Phantom Phone is a genuine Google Pixel, purchased new, then
              wiped and re-flashed with a hardened <span className="text-purple-300">GrapheneOS</span>{" "}
              build at the privacy tier of your choice —{" "}
              <span className="text-white/90">Degoogled, Stealth, or Phantom</span>.
            </p>

            <div className="mt-10 flex items-center gap-3 flex-wrap">
              <Link to="/shop" className="btn-primary" data-testid="phones-shop-cta">
                Browse the range <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/tiers" className="btn-ghost" data-testid="phones-tiers-cta">
                Compare tiers
              </Link>
            </div>

            <div className="mt-8 grid grid-cols-3 gap-4 max-w-md">
              <MiniStat icon={ShieldCheck} value="21" label="Pixel models" />
              <MiniStat icon={Lock} value="3" label="Privacy tiers" />
              <MiniStat icon={Cpu} value="100%" label="Australian built" />
            </div>
          </div>

          {/* Logo hero card */}
          <div className="relative">
            <div className="relative rounded-3xl overflow-hidden aspect-square p-10 flex items-center justify-center bg-black border border-purple-500/20">
              <div className="absolute inset-0 aurora-purple opacity-60" />
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-transparent to-purple-900/20" />
              <img
                src="/img/phantom-phone-logo.png"
                alt="Phantom Phone — Phantom Industries"
                className="relative w-full h-full object-contain drop-shadow-[0_0_60px_rgba(168,85,247,0.45)]"
                data-testid="phones-logo"
              />
            </div>
            <div className="absolute -bottom-4 -left-4 md:-left-8 card-glass rounded-2xl px-5 py-3 border border-purple-500/40">
              <div className="text-[10px] uppercase tracking-[0.2em] text-purple-300">
                GrapheneOS · verified boot
              </div>
              <div className="font-display text-lg font-bold text-white mt-0.5">
                Signed by us. Attested by hardware.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Pixels */}
      <section className="relative py-24 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between gap-6 flex-wrap">
            <div>
              <div className="chip"><Fingerprint className="h-3 w-3" /> The line-up</div>
              <h2 className="mt-4 font-headline text-4xl md:text-5xl font-bold text-white">
                Pick your Pixel. Pick your Phantom.
              </h2>
            </div>
            <Link to="/shop" className="btn-ghost">
              View all 21 <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="mt-10 grid md:grid-cols-3 gap-4">
            {featured.map((p) => (
              <Link
                key={p.id}
                to={`/product/${p.id}`}
                className="card-glass rounded-2xl overflow-hidden hover:border-purple-500/40 transition-colors group"
                data-testid={`phones-featured-${p.id}`}
              >
                <div className="relative aspect-square bg-black overflow-hidden">
                  <div
                    className="absolute inset-0 opacity-90 group-hover:scale-105 transition-transform duration-700"
                    style={{
                      backgroundImage: `url(${p.image})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                </div>
                <div className="p-5">
                  <div className="font-display text-xl font-semibold text-white">{p.name}</div>
                  <div className="text-xs text-purple-300 mt-1">{p.tagline}</div>
                  <div className="mt-3 text-sm text-white/60">
                    From <span className="text-white font-semibold">{formatAUD(priceFor(p.base_aud, TIERS[0].id))}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="relative py-24 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-3 gap-6">
          <Pillar icon={EyeOff} title="Zero Google. Zero drift."
            body="We remove every Google service, every telemetry hook, every ad-ID surface at the OS level. Nothing pings home." />
          <Pillar icon={ShieldCheck} title="Verified boot, every boot."
            body="Locked bootloader, our signature, hardware attestation on every startup. Tamper with it and it won't boot." />
          <Pillar icon={Lock} title="Three tiers. One promise."
            body="Degoogled for privacy-curious. Stealth for high-risk users. Phantom for people who need to vanish. All lock down the same core surface." />
        </div>
      </section>

      {/* Tier preview */}
      <section className="relative py-24 border-b border-white/5 overflow-hidden">
        <div
          className="absolute inset-0 opacity-25 pointer-events-none bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMAGES.darkTech})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black via-black/80 to-black" />
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto">
            <div className="chip mx-auto">
              <Lock className="h-3 w-3" /> Three tiers, one promise
            </div>
            <h2 className="mt-5 font-headline text-4xl md:text-5xl font-bold text-gradient-purple">
              Pick your level of invisible.
            </h2>
            <p className="mt-4 text-white/60">
              Every device leaves us configured to your chosen tier — tested,
              serialized and shipped in tamper-evident packaging.
            </p>
          </div>

          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {TIERS.map((t, idx) => (
              <div key={t.id} className={`relative rounded-3xl p-8 card-glass ${t.glow} hover-lift flex flex-col`}>
                {t.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 chip bg-purple-500 text-black border-purple-400">
                    Most chosen
                  </div>
                )}
                <div className="flex items-center gap-3">
                  {idx === 0 && <ShieldCheck className="h-6 w-6" style={{ color: t.color }} />}
                  {idx === 1 && <EyeOff className="h-6 w-6" style={{ color: t.color }} />}
                  {idx === 2 && <Ghost className="h-6 w-6" style={{ color: t.color }} />}
                  <div className="font-display text-2xl font-bold" style={{ color: t.color }}>
                    {t.name}
                  </div>
                </div>
                <div className="text-xs uppercase tracking-[0.2em] text-white/40 mt-2">
                  {t.tagline}
                </div>
                <div className="mt-6 flex items-baseline gap-2">
                  <span className="text-white/50 text-sm">+</span>
                  <span className="font-display text-4xl font-bold text-white">
                    {formatAUD(t.price_add)}
                  </span>
                  <span className="text-white/50 text-sm">on device</span>
                </div>
                <ul className="mt-6 space-y-2.5 flex-1">
                  {t.highlights.slice(0, 5).map((h) => (
                    <li key={h} className="flex items-start gap-3 text-sm text-white/70">
                      <span className="mt-1.5 h-1 w-1 rounded-full bg-purple-400 flex-shrink-0" />
                      {h}
                    </li>
                  ))}
                </ul>
                <Link to={`/tiers#${t.id}`} className="mt-6 inline-flex items-center gap-2 text-sm text-purple-300 hover:text-white transition-colors">
                  Full specification <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Accessories preview */}
      <section className="relative py-24 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <div className="chip mb-4">
                <Cpu className="h-3 w-3" /> Beyond the phone
              </div>
              <h2 className="font-headline text-4xl md:text-5xl font-bold text-gradient-purple">
                Encrypted USBs, cases, merch.
              </h2>
              <p className="mt-3 text-white/60 max-w-xl">
                Hardened boot media, hardware-encrypted vaults, phone cases and
                the unofficial Phantom uniform.
              </p>
            </div>
            <Link to="/shop" className="hidden md:inline-flex items-center gap-2 text-sm text-purple-300 hover:text-white transition-colors">
              Shop accessories <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {ACCESSORIES.slice(0, 4).map((a) => (
              <Link
                key={a.id}
                to={`/product/${a.id}`}
                className="group relative rounded-3xl overflow-hidden card-glass hover-lift"
              >
                <div className="aspect-square relative">
                  <div
                    className="absolute inset-0 opacity-90 group-hover:scale-105 transition-transform duration-700"
                    style={{
                      backgroundImage: `url(${a.image})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      ...(a.imageStyle || {}),
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                </div>
                <div className="p-4">
                  <div className="font-display font-semibold text-white">{a.name}</div>
                  <div className="text-xs text-purple-300 mt-1">
                    From {formatAUD(Math.min(...a.variants.map((v) => v.price)))}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Cross-sell to Phantom X */}
      <section className="py-16 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="card-glass rounded-3xl p-8 md:p-12 flex flex-col md:flex-row items-center gap-8 border border-purple-500/20">
            <div className="flex-1">
              <div className="text-xs text-white/40 uppercase tracking-[0.2em]">Also from Phantom Industries</div>
              <div className="mt-3 font-headline text-3xl md:text-4xl font-bold text-white leading-tight">
                Pair your Phantom Phone with<br />Phantom X.
              </div>
              <div className="mt-3 text-sm text-white/60 max-w-lg">
                Encrypted messaging that only works while a physical Phantom Key is
                plugged in. Anonymous by design.
              </div>
            </div>
            <Link to="/chat" className="btn-primary flex-shrink-0" data-testid="phones-crosssell-chat">
              <MessageSquare className="h-4 w-4" /> Explore Phantom X <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

const MiniStat = ({ icon: Icon, value, label }) => (
  <div>
    <div className="flex items-center gap-1.5 text-purple-300">
      <Icon className="h-3.5 w-3.5" />
      <div className="font-display text-2xl font-bold text-white">{value}</div>
    </div>
    <div className="text-xs text-white/40 mt-1">{label}</div>
  </div>
);

const Pillar = ({ icon: Icon, title, body }) => (
  <div className="card-glass rounded-2xl p-6">
    <div className="h-10 w-10 rounded-xl bg-purple-500/10 border border-purple-500/30 flex items-center justify-center">
      <Icon className="h-5 w-5 text-purple-300" />
    </div>
    <div className="mt-5 font-display text-lg font-semibold text-white">{title}</div>
    <div className="mt-2 text-sm text-white/60 leading-relaxed">{body}</div>
  </div>
);

export default Phones;
